// Whether the signed-in user gets the "RBP Shopping List" entry point, in a Zustand store. One
// cheap read after sign-in (`GET /api/v1/rbp/eligibility`, `startRbpEligibilityLoadOnAuth`, wired
// at the composition root as the HHT waiting count is). The server decides (participant store,
// store-user persona, never licence trade), so the SPA only reads the answer. A failed or refused
// read degrades silently to `false`: no entry point rather than an error, and sign-in never waits
// on it.

import { create } from 'zustand';
import { fetchRbpEligibility } from '../api/rbp.client';
import { useAuthStore } from '../auth/useAuthStore';

interface RbpEligibilityState {
  readonly shoppingList: boolean;
  /** (Re)read the signal. Concurrent calls share the request in flight. Never rejects. */
  load: () => Promise<void>;
  /** Session hygiene: back to "no entry point" (sign-out, account switch). */
  reset: () => void;
}

let inFlight: Promise<void> | null = null;
// Bumped by `reset`, so a read started before a sign-out cannot land after it.
let generation = 0;

export const useRbpEligibilityStore = create<RbpEligibilityState>((set) => ({
  shoppingList: false,

  load() {
    if (inFlight !== null) return inFlight;
    const started = generation;
    const run: Promise<void> = (async () => {
      try {
        const shoppingList = await fetchRbpEligibility();
        if (started === generation) set({ shoppingList });
      } catch {
        if (started === generation) set({ shoppingList: false });
      }
    })();
    inFlight = run;
    void run.finally(() => {
      if (inFlight === run) inFlight = null;
    });
    return run;
  },

  reset() {
    generation += 1;
    inFlight = null;
    set({ shoppingList: false });
  },
}));

/**
 * Load the signal exactly once, the first time the session settles into `ready &&
 * isAuthenticated`. Returns an unsubscribe handle (for tests). Fires at once if the session is
 * already authenticated when wired.
 */
export function startRbpEligibilityLoadOnAuth(): () => void {
  let fired = false;
  const maybeLoad = (): void => {
    if (fired) return;
    const { status, isAuthenticated } = useAuthStore.getState();
    if (status !== 'ready' || !isAuthenticated) return;
    fired = true;
    unsubscribe();
    void useRbpEligibilityStore.getState().load();
  };
  const unsubscribe = useAuthStore.subscribe(maybeLoad);
  maybeLoad();
  return unsubscribe;
}
